import {ISODateFormat} from 'src/types/onboarding';
import {DataService, Req} from 'src/services/types';
import {parseISO} from 'date-fns';
import {Point} from 'src/types/util';
import {cloneDeep, identity} from 'lodash';

export enum IntervalType {
  WEEK = 'week',
  MONTH = 'month',
}

export interface Interval {
  step: IntervalType;
  from: string;
  to: string;
}

export type NumericSeriesPoint = Point<Date, number>;
export interface Series<SeriesPoint, Meta extends object = Record<string, never>> {
  meta: Meta;
  series: Record<string, SeriesPoint[]>;
}

export const formatResponse = <Meta extends object = Record<string, never>>(
  response: SeriesResponse,
  initialData: Series<NumericSeriesPoint, Meta>,
  buildMeta: (meta: Meta, index: number, response: SeriesResponse) => Meta = identity
): Series<NumericSeriesPoint, Meta> => {
  const seriesColumns = Object.keys(response.series);
  const data: Series<NumericSeriesPoint, Meta> = cloneDeep(initialData);
  for (let i = 0; i < response.dates.length; i++) {
    const parsedISODate = parseISO(response.dates[i]);
    seriesColumns.forEach((column) => {
      const point: NumericSeriesPoint = {x: parsedISODate, y: response.series[column][i]};
      data.series[column].push(point);
    });

    data.meta = buildMeta(data.meta, i, response);
  }

  return data;
};

export interface SeriesResponse {
  interval: Interval;
  dates: ISODateFormat[];
  series: Record<string, number[]>;
}

export abstract class SeriesDataService<Meta extends object = Record<string, never>>
  implements DataService<Series<NumericSeriesPoint, Meta>>
{
  abstract dataUrl: string;
  abstract initialData: Series<NumericSeriesPoint, Meta>;

  buildMeta: (meta: Meta, index: number, response: SeriesResponse) => Meta = identity;

  fetch = async (req: Req): Promise<Series<NumericSeriesPoint, Meta>> => {
    const result = await req({url: this.dataUrl});
    return formatResponse<Meta>(result, this.initialData, this.buildMeta);
  };
}
